import {
  FontIcons,
  SearchTextfield,
  StandardCombobox,
  brandfolderColorPalette,
} from '@brandfolder/react';
import { t } from '@lingui/macro';
import React, { ChangeEvent, FunctionComponent, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { PeopleSortType } from '@components/people_tags/customPeopleTagTypes';

import { usePeopleManagementContext } from './PeopleManagementContext';

const PeopleActionsBarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-bottom: 16px;

  @media (min-width: 900px) {
    flex-direction: row;
  }
`;

const SearchPeopleTextfield = styled(SearchTextfield)`
  @media (min-width: 900px) {
    width: 50%;
  }
`;

const ActionBarDropdownWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 20px 0;

  .filter-people-dropdown,
  .sort-people-dropdown {
    position: relative;
    width: 48.5%;
    z-index: 10;
  }

  @media (min-width: 744px) {
    .filter-people-dropdown {
      margin-right: 10px;
    }
    .sort-people-dropdown {
      margin-left: 10px;
      width: 70%;
    }
  }

  @media (min-width: 900px) {
    margin: 0 0 20px 20px;
    width: 48.5%;
  }

  @media (min-width: 1526px) {
    .filter-people-dropdown {
      width: 40%;
    }
    .sort-people-dropdown {
      width: 60%;
    }
  }
`;

const StyledCombobox = styled(StandardCombobox)`
  .bf-combobox {
    &[aria-expanded='true'],
    &:active,
    &:focus-visible {
      background-color: transparent;
      border: 2px solid ${brandfolderColorPalette.primary[600]};
    }
  }

  .bff-button__icon {
    &:hover {
      background-color: transparent;
    }
  }

  .bf-list {
    max-height: unset;

    .bf-listitem {
      border-radius: 4px;
      color: ${brandfolderColorPalette.neutrals[700]};

      &[aria-selected='true'] {
        background-color: ${brandfolderColorPalette.neutrals[100]};
      }
    }
  }
`;

const sortOptions = [
  { key: PeopleSortType.NamedFirst, label: t`Sort by...` },
  { key: PeopleSortType.UploadDateDesc, label: t`Upload date (newest)` },
  { key: PeopleSortType.UploadDateAsc, label: t`Upload date (oldest)` },
  { key: PeopleSortType.FrequencyDesc, label: t`Frequency (appears most)` },
  { key: PeopleSortType.FrequencyAsc, label: t`Frequency (appears least)` },
  { key: PeopleSortType.LastNameAsc, label: t`Last name (A-Z)` },
  { key: PeopleSortType.LastNameDesc, label: t`Last name (Z-A)` },
  { key: PeopleSortType.FirstNameAsc, label: t`First name (A-Z)` },
  { key: PeopleSortType.FirstNameDesc, label: t`First name (Z-A)` },
];

const createOptionMap = (options: { key: string; label: string }[]): Record<string, string> =>
  options.reduce((obj, item) => (obj[item.key] = item.label, obj), {});

export const PeopleManagementActionsBar: FunctionComponent = () => {
  const { state, updateSearch, updateSort } = usePeopleManagementContext();

  const setSearchState = (e: ChangeEvent<HTMLInputElement>): void => {
    updateSearch(e.target.value);
  };

  const sortOptionsMap = useMemo(() => createOptionMap(sortOptions), []);

  const selectedSortIndex = sortOptions.findIndex(({ label }) => {
    return label === sortOptionsMap[state.sort];
  });

  const sortComboboxLabels = useMemo(
    () => ({
      listboxLabel: t`List of sort options`,
      iconButtonLabel: t`Toggle list of sort options`,
      label: t`Sort options`,
    }),
    []
  );

  const sortComboboxOptions = useMemo(
    () =>
      sortOptions.map(({ key, label }) => ({
        children: <>{label}</>,
        key,
        value: label,
      })),
    []
  );

  const handleSortSelection = useCallback(
    (option: { key: string | number } | null): void => {
      if (option?.key != null) {
        updateSort(String(option.key));
      }
    },
    [updateSort]
  );

  return (
    <PeopleActionsBarWrapper>
      <SearchPeopleTextfield
        id="search-textfield"
        label={t`Search for people textfield`}
        onChange={setSearchState}
        placeholder={t`Search for people`}
        showLabel={false}
        textfieldProps={{
          autoComplete: 'off',
        }}
        value={state.search}
      />
      <ActionBarDropdownWrapper>
        <StyledCombobox
          className="sort-people-dropdown"
          id="sort-dropdown"
          initialSelectedIndex={selectedSortIndex}
          labels={sortComboboxLabels}
          leftIcon={FontIcons.ArrowUpDown}
          onSelection={handleSortSelection}
          options={sortComboboxOptions}
          showEmptyOption
          showLabel={false}
          showPlaceholderOption={false}
        />
      </ActionBarDropdownWrapper>
    </PeopleActionsBarWrapper>
  );
};
